/**
 * CourseRow Component
 * Horizontal scrolling row of courses (Netflix-style)
 */

import { useRef } from 'react';
import type { Course } from '../../types/academy';
import { CourseCard } from './CourseCard';
import { Icons } from '../Icons';

interface CourseRowProps {
  title: string;
  courses: Course[];
  icon?: string;
  seeAllLink?: string;
  userProgress?: Record<string, number>; // courseId -> progress percentage
  myList?: string[]; // courseIds in user's list
  onPlayCourse?: (course: Course) => void;
  onAddToList?: (courseId: string) => void;
  onCourseInfo?: (course: Course) => void;
}

export function CourseRow({
  title,
  courses,
  icon,
  seeAllLink,
  userProgress = {},
  myList = [],
  onPlayCourse,
  onAddToList,
  onCourseInfo
}: CourseRowProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      const newScrollLeft = direction === 'left'
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  if (courses.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: '48px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingLeft: '20px',
        paddingRight: '20px'
      }}>
        <h2 style={{
          fontSize: '22px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {icon && <span>{icon}</span>}
          {title}
        </h2>

        {seeAllLink && (
          <a
            href={seeAllLink}
            style={{
              color: 'var(--accent)',
              fontSize: '14px',
              fontWeight: 600,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            See All
            <Icons.ChevronRight size={16} />
          </a>
        )}
      </div>

      {/* Carousel */}
      <div style={{ position: 'relative', paddingLeft: '20px', paddingRight: '20px' }}>
        {/* Left Arrow */}
        <button
          onClick={() => scroll('left')}
          style={{
            position: 'absolute',
            left: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.8)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            transition: 'all 0.2s',
            opacity: 0.7
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.7';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
        >
          <Icons.ChevronLeft size={24} />
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          style={{
            position: 'absolute',
            right: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.8)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            transition: 'all 0.2s',
            opacity: 0.7
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.7';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
        >
          <Icons.ChevronRight size={24} />
        </button>

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
            paddingBottom: '8px'
          }}
          className="hide-scrollbar"
        >
          {courses.map((course) => (
            <div
              key={course.id}
              style={{
                minWidth: '280px',
                maxWidth: '280px'
              }}
            >
              <CourseCard
                course={course}
                progress={userProgress[course.id]}
                isInList={myList.includes(course.id)}
                onPlay={() => onPlayCourse?.(course)}
                onAddToList={() => onAddToList?.(course.id)}
                onInfo={() => onCourseInfo?.(course)}
              />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
